import { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import { useBreadcrumb } from '../layouts/BreadcrumbContext';
import InlineLoader from '../components/Loader/InlineLoader';
import PatientImage from '../components/Patient/PatientImage';
import PaymentMethodBadge from '../components/Order/PaymentMethodBadge';
import FabGroup from '../components/FabGroup';
import Modal from '../components/Modal/Modal';
import { SECTION_ICON_STYLE } from '../components/SectionCard/sectionIconStyle';
import { Document, Page, pdfjs } from 'react-pdf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave, faCircleCheck, faClock, faXmark,
    faInfoCircle, faUser, faUserSlash, faPrint, faCoins, faDownload,
    faMoneyBillTransfer, faCalendarDays, faFileInvoice, faHandHoldingDollar,
    faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../api';
import { formatRelativeDate, formatFullDate } from '../utils/FormatDate';
import useRecordNeighbors from '../hooks/useRecordNeighbors';
import RecordNav from '../components/RecordNav/RecordNav';
import './InvoiceDetails.scss';

import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

const fmt = (val) =>
    new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

const getStatusMap = (t) => ({
    CONFIRMED: { label: t('Confirmed'),                 cls: 'inv-badge--green',   icon: faCircleCheck },
    PENDING:   { label: t('Pending'),                   cls: 'inv-badge--amber',   icon: faClock       },
    FAILED:    { label: t('payment_details.status.failed'), cls: 'inv-badge--red', icon: faXmark       },
});

const FieldRow = ({ label, children }) => (
    <div className="inv-field-row">
        <span className="inv-field-label">{label}</span>
        <span className="inv-field-value">{children}</span>
    </div>
);

const paymentPath = (targetId) => `/payments/${targetId}`;

export default function PaymentDetails() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [payment, setPayment] = useState(null);
    const [loading, setLoading] = useState(true);
    const { setTitle } = usePageTitle();
    const { setBreadcrumb } = useBreadcrumb();

    const [pdfData,      setPdfData]      = useState(null);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [numPages,     setNumPages]     = useState(null);
    const [pageNumber,   setPageNumber]   = useState(1);
    const [invoice,      setInvoice]      = useState(null);

    // Previous / next payment (header arrows + ← → keys)
    const { neighbors, goTo } = useRecordNeighbors(`/api/v1/payment/${id}/neighbors`, paymentPath);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/v1/payment/${id}`, { withCredentials: true })
            .then(({ data }) => {
                setPayment(data);
                setTitle(data.paymentNumber);
                document.title = data.paymentNumber;
                setBreadcrumb([
                    { label: t('Payments'), url: '/payments' },
                    { label: data.paymentNumber },
                ]);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [id, t]);

    useEffect(() => {
        if (!payment) return;
        if (payment.invoiceId) {
            axios.get(`${API_BASE_URL}/api/v1/invoice/${payment.invoiceId}`, { withCredentials: true })
                .then(({ data }) => setInvoice(data))
                .catch(() => {});
        } else if (payment.invoiceNumber) {
            // Repli : retrouver la facture par son numéro
            axios.get(`${API_BASE_URL}/api/v1/invoices`, {
                withCredentials: true,
                params: { page: 0, size: 1, keyword: payment.invoiceNumber },
            })
                .then(({ data }) => {
                    const found = (data.content ?? [])[0];
                    if (found?.invoiceNumber === payment.invoiceNumber) setInvoice(found);
                })
                .catch(() => {});
        }
    }, [payment]);

    const handleDownloadPdf = () => {
        axios.get(`${API_BASE_URL}/api/v1/payment/${id}/receipt`, {
            withCredentials: true,
            responseType: 'blob',
        })
            .then((response) => {
                const fileURL = URL.createObjectURL(
                    new Blob([response.data], { type: 'application/pdf' })
                );
                const link = document.createElement('a');
                link.href = fileURL;
                link.download = `recu-${payment?.paymentNumber ?? id}.pdf`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(fileURL);
            })
            .catch((error) => console.error('Erreur téléchargement reçu :', error));
    };

    const handleViewPdf = () => {
        axios.get(`${API_BASE_URL}/api/v1/payment/${id}/receipt`, {
            withCredentials: true,
            responseType: 'blob',
        })
            .then((response) => {
                const fileURL = URL.createObjectURL(
                    new Blob([response.data], { type: 'application/pdf' })
                );
                setPdfData(fileURL);
                setShowPdfModal(true);
                setPageNumber(1);
            })
            .catch((error) => console.error('Erreur reçu PDF :', error));
    };

    if (loading) return <InlineLoader loadingMessage={t('Loading...')} />;
    if (!payment) return <div className="page-content text-muted p-4">{t('payment_details.not_found')}</div>;

    const STATUS_MAP = getStatusMap(t);
    const status = STATUS_MAP[payment.status] ?? { label: payment.status ?? '—', cls: 'inv-badge--neutral', icon: faClock };

    const paymentDate = payment.paymentDate ? formatRelativeDate(payment.paymentDate) : '—';

    const isCash = payment.paymentMethod === 'CASH' && payment.cashGiven != null;
    const change = isCash ? (parseFloat(payment.cashGiven) - parseFloat(payment.amount)) : 0;

    const fabButtons = [
        {
            id: 'print',
            label: t('payment_details.action.print'),
            icon: faPrint,
            onClick: handleViewPdf,
        },
        {
            id: 'download',
            label: t('payment_details.action.download'),
            icon: faDownload,
            onClick: handleDownloadPdf,
        },
    ];

    return (
        <div className="page-content inv-detail">

            <FabGroup buttons={fabButtons} />

            {/* ── Header card ── */}
            <div className="inv-header-card inv-header-card--hero">
                <div className="inv-header-body">
                    <div className="inv-avatar" style={{ background: '#ecfdf5', color: '#059669' }}>
                        <FontAwesomeIcon icon={faMoneyBillWave} />
                    </div>
                    <div>
                        <p className="inv-number">{payment.paymentNumber}</p>
                        <p className="inv-date" data-tooltip-id="tooltip" data-tooltip-content={payment.paymentDate ? formatFullDate(payment.paymentDate) : undefined}>{paymentDate}</p>
                        <span className={`inv-badge ${status.cls}`}>
                            <FontAwesomeIcon icon={status.icon} />
                            {status.label}
                        </span>
                    </div>
                    <RecordNav neighbors={neighbors} onNavigate={goTo} noun="Paiement" />
                </div>

                {/* ── Totaux ── */}
                <div className="inv-totals-grid">
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faMoneyBillWave} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('Amount')}</div>
                            <div className="inv-total-value" style={{ color: '#059669' }}>
                                {fmt(payment.amount)} <small>dhs</small>
                            </div>
                        </div>
                    </div>
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faMoneyBillTransfer} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('Method')}</div>
                            <div style={{ marginTop: 4 }}>
                                {payment.paymentMethod
                                    ? <PaymentMethodBadge method={payment.paymentMethod} />
                                    : <span style={{ fontSize: 13, color: '#9ca3af' }}>—</span>}
                            </div>
                        </div>
                    </div>
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faCalendarDays} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('payment_details.field.invoice_date')}</div>
                            <div
                                className="inv-total-value"
                                style={{ fontSize: 14 }}
                                data-tooltip-id="tooltip"
                                data-tooltip-content={(invoice?.invoiceDate || invoice?.invoiceCreatedAt) ? formatFullDate(invoice.invoiceDate ?? invoice.invoiceCreatedAt) : undefined}
                            >
                                {(invoice?.invoiceDate || invoice?.invoiceCreatedAt)
                                    ? formatRelativeDate(invoice.invoiceDate ?? invoice.invoiceCreatedAt)
                                    : <span style={{ color: '#9ca3af' }}>—</span>}
                            </div>
                        </div>
                    </div>
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faFileInvoice} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('payment_details.field.invoice_total')}</div>
                            <div className="inv-total-value">
                                {invoice?.totalAmount != null
                                    ? <>{fmt(invoice.totalAmount)} <small>dhs</small></>
                                    : <span style={{ fontSize: 13, color: '#9ca3af' }}>—</span>}
                            </div>
                        </div>
                    </div>
                    {isCash && (
                        <>
                            <div className="inv-total-card">
                                <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                                    <FontAwesomeIcon icon={faCoins} />
                                </span>
                                <div>
                                    <div className="inv-total-label">{t('payment_details.field.cash_received')}</div>
                                    <div className="inv-total-value">{fmt(payment.cashGiven)} <small>dhs</small></div>
                                </div>
                            </div>
                            <div className="inv-total-card">
                                <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                                    <FontAwesomeIcon icon={faHandHoldingDollar} />
                                </span>
                                <div>
                                    <div className="inv-total-label">{t('payment_details.field.change')}</div>
                                    <div className="inv-total-value" style={{ color: change >= 0 ? '#059669' : '#dc2626' }}>
                                        {fmt(change)} <small>dhs</small>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* ── Info columns ── */}
            <div className="inv-col-layout">

                <div>
                    <div className="inv-section-card">
                        <div className="inv-section-title">
                            <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                                <FontAwesomeIcon icon={faInfoCircle} />
                            </span>
                            {t('Information')}
                        </div>
                        <FieldRow label={t('payment_details.field.payment_number')}>{payment.paymentNumber}</FieldRow>
                        <FieldRow label={t('Date')}>
                            <span data-tooltip-id="tooltip" data-tooltip-content={payment.paymentDate ? formatFullDate(payment.paymentDate) : undefined}>
                                {paymentDate}
                            </span>
                        </FieldRow>
                        <FieldRow label={t('Method')}>
                            {payment.paymentMethod
                                ? <PaymentMethodBadge method={payment.paymentMethod} />
                                : '—'}
                        </FieldRow>
                        {payment.invoiceNumber && (
                            <FieldRow label={t('Invoice')}>
                                {(payment.invoiceId ?? invoice?.id)
                                    ? (
                                        <NavLink
                                            to={`/invoices/${payment.invoiceId ?? invoice.id}`}
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                width: 28, height: 28, borderRadius: 8,
                                                background: '#f3f4f6', color: '#6b7280',
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#6b7280'; }}
                                            data-tooltip-id="tooltip"
                                            data-tooltip-content={payment.invoiceNumber}
                                        >
                                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: 12 }} />
                                        </NavLink>
                                    )
                                    : payment.invoiceNumber}
                            </FieldRow>
                        )}
                        <FieldRow label={t('Status')}>
                            <span className={`inv-badge ${status.cls}`}>
                                <FontAwesomeIcon icon={status.icon} /> {status.label}
                            </span>
                        </FieldRow>
                    </div>

                    {/* ── Patient ── */}
                    {payment.patient ? (
                        <div className="inv-section-card" style={{ marginTop: 12 }}>
                            <div className="inv-section-title">
                                <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                                    <FontAwesomeIcon icon={faUser} />
                                </span>
                                {t('Patient')}
                            </div>
                            <NavLink to={`/patient/${payment.patient.id}`} className="inv-patient-card">
                                <PatientImage patient={payment.patient} />
                                <div>
                                    <p className="inv-patient-name">
                                        {payment.patient.firstName} {payment.patient.lastName}
                                    </p>
                                    <p className="inv-patient-sub">{payment.patient.phoneNumber ?? '—'}</p>
                                </div>
                            </NavLink>
                        </div>
                    ) : (
                        <div className="inv-section-card" style={{ marginTop: 12 }}>
                            <div className="inv-section-title">
                                <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                                    <FontAwesomeIcon icon={faUser} />
                                </span>
                                {t('Patient')}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.75rem 1.25rem', color: '#9ca3af' }}>
                                <FontAwesomeIcon icon={faUserSlash} style={{ fontSize: 14 }} />
                                <span style={{ fontSize: 13 }}>{t('Anonymous Visitor')}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    {/* ── Détail financier ── */}
                    <div className="inv-section-card">
                        <div className="inv-section-title">
                            <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                                <FontAwesomeIcon icon={faCoins} />
                            </span>
                            {t('payment_details.section.financial_detail')}
                        </div>
                        {invoice?.totalAmount != null && (
                            <FieldRow label={t('payment_details.field.invoice_total')}>{fmt(invoice.totalAmount)} dhs</FieldRow>
                        )}
                        <FieldRow label={t('payment_details.field.amount_paid')}>
                            <strong style={{ color: '#059669' }}>{fmt(payment.amount)} dhs</strong>
                        </FieldRow>
                        {isCash && (
                            <>
                                <FieldRow label={t('payment_details.field.cash_received')}>{fmt(payment.cashGiven)} dhs</FieldRow>
                                <FieldRow label={t('payment_details.field.change')}>
                                    <span style={{ color: change >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                                        {fmt(change)} dhs
                                    </span>
                                </FieldRow>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Receipt PDF modal ── */}
            <Modal
                title={t('payments.receipt_modal_title')}
                show={showPdfModal}
                onClose={() => { setShowPdfModal(false); setPdfData(null); }}
                classSize="modal-lg"
            >
                {pdfData ? (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Document file={pdfData} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
                                <Page pageNumber={pageNumber} width={680} />
                            </Document>
                        </div>
                        {numPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <button className="btn btn-outline-primary btn-sm"
                                    disabled={pageNumber <= 1} onClick={() => setPageNumber(pageNumber - 1)}>
                                    {t('Previous')}
                                </button>
                                <span className="text-muted small">{t('pdf_viewer.page_indicator', { page: pageNumber, total: numPages })}</span>
                                <button className="btn btn-outline-primary btn-sm"
                                    disabled={pageNumber >= numPages} onClick={() => setPageNumber(pageNumber + 1)}>
                                    {t('Next')}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-4 text-muted">{t('payments.receipt_loading')}</div>
                )}
            </Modal>
        </div>
    );
}
